import {
  createFileRoute,
  Outlet,
  redirect,
  useChildMatches,
  useNavigate,
} from '@tanstack/react-router'
import type { CustomerReturnFilters } from '@/features/sales-returns/api'
import { ReturnList } from '@/features/sales-returns/return-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the customer return list. `returns.new.tsx` and
 * `returns.$returnId.index.tsx` are CHILDREN of this route in flat file
 * routing — without the Outlet below their URLs render this list instead of
 * themselves (apps/company/AGENTS.md, mirrors
 * `_authenticated/purchasing/returns.tsx`).
 */
export const Route = createFileRoute('/_authenticated/sales/returns')({
  validateSearch: (search: Record<string, unknown>): CustomerReturnFilters => ({
    customer: typeof search.customer === 'string' && search.customer ? search.customer : undefined,
    warehouse:
      typeof search.warehouse === 'string' && search.warehouse ? search.warehouse : undefined,
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
  }),
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'customer_return.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: SalesReturnsRoute,
})

function SalesReturnsRoute() {
  const navigate = useNavigate()
  const children = useChildMatches()
  const params = Route.useSearch()

  if (children.length > 0) {
    return <Outlet />
  }

  return (
    <ReturnList
      params={params}
      onParamsChange={(next) => void navigate({ to: '/sales/returns', search: next })}
    />
  )
}
